import { useState } from "react";
import { FitText } from "@/components/fit-text";
import { usePageTracking } from "@/hooks/use-track";
import { usePageTour } from "@/components/ProductTour";
import { TutorialTip } from "@/components/TutorialTip";
import { HelpTip } from "@/components/help-tip";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/status-badge";
import { EmptyState } from "@/components/empty-state";
import { DashboardSkeleton } from "@/components/loading-skeleton";
import { NewQuoteModal, useNewQuoteModal } from "@/components/new-quote-modal";
import { Link } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { formatMoney, getMarketCurrency } from "@/lib/utils";
import { 
  FileText, 
  Users, 
  TrendingUp, 
  PoundSterling,
  Plus,
  ArrowRight,
  Clock,
  Timer,
  Zap,
  Pencil,
  Check,
  X,
  Gift,
  BarChart2,
} from "lucide-react";
import { getQuotaInfo } from "@/lib/quota-banners";
import type { Quote } from "@shared/schema";

interface DashboardStats {
  totalQuotes: number;
  totalClients: number;
  totalValue: number;
  acceptedRate: number;
}

interface TimeSavingsData {
  quoteCount: number;
  totalTraditionalMinutes: number;
  totalJobioMinutes: number;
  timeSavedMinutes: number;
  timeSavedHours: number;
  hourlyRate: number;
  moneySaved: number;
}

interface RecentQuote extends Quote {
  clientName?: string;
}

function formatTimeSaved(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMin = minutes % 60;
  if (remainingMin === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remainingMin}m`;
}

function TimeSavingsWidget() {
  const { toast } = useToast();
  const { user } = useAuth();
  const currencySymbol = getMarketCurrency(user?.market).symbol;
  const [editingRate, setEditingRate] = useState(false);
  const [rateInput, setRateInput] = useState("");

  const { data: savings, isLoading } = useQuery<TimeSavingsData>({
    queryKey: ["/api/stats/time-savings"],
  });

  const rateMutation = useMutation({
    mutationFn: async (hourlyRate: number) => {
      const res = await apiRequest("PUT", "/api/profile/hourly-rate", { hourlyRate });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/stats/time-savings"] });
      setEditingRate(false);
      toast({ title: "Hourly rate updated" });
    },
    onError: () => {
      toast({ title: "Failed to update rate", variant: "destructive" });
    },
  });

  const handleEditRate = () => {
    setRateInput(String(savings?.hourlyRate || 45));
    setEditingRate(true);
  };

  const handleSaveRate = () => {
    const val = parseFloat(rateInput);
    if (isNaN(val) || val < 0) {
      toast({ title: "Please enter a valid hourly rate", variant: "destructive" });
      return;
    }
    rateMutation.mutate(val);
  };

  const formatCurrency = (value: number) => formatMoney(value, user?.market, { decimals: 0 });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-1/3" />
            <div className="h-8 bg-muted rounded w-1/2" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!savings || savings.quoteCount === 0) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
          <CardTitle className="text-sm font-medium">Time & Money Saved</CardTitle>
          <Zap className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Create your first quote to start tracking time and money saved with The Proper Tool.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="card-time-savings">
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <Zap className="h-5 w-5 text-primary shrink-0" />
          Time & Money Saved
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
              <Timer className="h-3.5 w-3.5" />
              Time Saved
            </p>
            <p className="text-2xl font-bold" data-testid="text-time-saved">
              {formatTimeSaved(savings.timeSavedMinutes)}
            </p>
            <p className="text-xs text-muted-foreground">
              Across {savings.quoteCount} quote{savings.quoteCount !== 1 ? "s" : ""}
            </p>
          </div>

          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
              <PoundSterling className="h-3.5 w-3.5" />
              Money Saved
            </p>
            <p className="text-2xl font-bold text-primary" data-testid="text-money-saved">
              {formatCurrency(savings.moneySaved)}
            </p>
            <p className="text-xs text-muted-foreground">
              Based on your hourly rate
            </p>
          </div>

          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Your Hourly Rate</p>
            {editingRate ? (
              <div className="flex items-center gap-1.5">
                <div className="relative flex-1">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">{currencySymbol}</span>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={rateInput}
                    onChange={(e) => setRateInput(e.target.value)}
                    className={`${currencySymbol.length >= 2 ? "pl-9" : "pl-7"} h-9`}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveRate();
                      if (e.key === "Escape") setEditingRate(false);
                    }}
                    data-testid="input-hourly-rate"
                  />
                </div>
                <Button size="icon" variant="ghost" onClick={handleSaveRate} disabled={rateMutation.isPending} data-testid="button-save-rate">
                  <Check className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => setEditingRate(false)} data-testid="button-cancel-rate">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold" data-testid="text-hourly-rate">
                  {formatCurrency(savings.hourlyRate)}
                </p>
                <span className="text-xs text-muted-foreground">/hr</span>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={handleEditRate}
                  className="h-7 w-7"
                  data-testid="button-edit-rate"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="pt-2 border-t">
          <div className="flex items-center justify-between gap-4 text-xs text-muted-foreground flex-wrap">
            <span>
              Traditional quoting: ~{formatTimeSaved(savings.totalTraditionalMinutes)}
            </span>
            <span>
              With The Proper Tool: ~{formatTimeSaved(savings.totalJobioMinutes)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PlanUsageCard() {
  const { user } = useAuth();
  const quotaInfo = getQuotaInfo(user as any);

  if (!quotaInfo) return null;

  const isTrial = quotaInfo.period === "trial";
  const progressPct = Math.min(100, Math.round((quotaInfo.quotesUsed / quotaInfo.quotesTotal) * 100));
  const isNearLimit = quotaInfo.quotesLeft <= 2;

  const trialDaysLeft =
    isTrial && user?.trialEndAt
      ? Math.max(0, Math.ceil((new Date(user.trialEndAt as unknown as string).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
      : null;

  const resetDate = !isTrial
    ? (user as any)?.monthlyQuotaResetAt
      ? new Date((user as any).monthlyQuotaResetAt).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
    : null;

  return (
    <Card data-testid="card-plan-usage">
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3">
        <CardTitle className="text-base sm:text-lg flex items-center gap-2">
          <BarChart2 className="h-5 w-5 text-primary shrink-0" />
          {isTrial ? "Free Trial" : "Monthly Usage"}
        </CardTitle>
        {isTrial && trialDaysLeft !== null && (
          <span className="text-sm text-muted-foreground">
            {trialDaysLeft === 0
              ? "Expires today"
              : `${trialDaysLeft} day${trialDaysLeft !== 1 ? "s" : ""} remaining`}
          </span>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {isTrial ? "Trial quotes" : "Quotes this month"}
            </span>
            <span className={`font-medium ${isNearLimit ? "text-amber-600 dark:text-amber-400" : ""}`}>
              {quotaInfo.quotesLeft} of {quotaInfo.quotesTotal} remaining
            </span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${isNearLimit ? "bg-amber-500" : "bg-primary"}`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
        {!isTrial && resetDate && (
          <p className="text-xs text-muted-foreground">Resets {resetDate}</p>
        )}
        {!isTrial && isNearLimit && (
          <a href="/settings" className="text-xs font-medium text-primary hover:underline" data-testid="link-upgrade-paid-near-limit">
            You're almost out of quotes for this month. Upgrade to Pro for 25 quotes/month →
          </a>
        )}
        {isTrial && (
          <a href="/settings" className="text-xs font-medium text-primary hover:underline">
            Upgrade to a paid plan →
          </a>
        )}
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  usePageTracking("Dashboard");
  usePageTour("dashboard");
  const { user } = useAuth();
  const { open: quoteModalOpen, openModal: openQuoteModal, closeModal: closeQuoteModal } = useNewQuoteModal();

  const { data: stats, isLoading: statsLoading } = useQuery<DashboardStats>({
    queryKey: ["/api/stats"],
  });

  const { data: recentQuotes, isLoading: quotesLoading } = useQuery<RecentQuote[]>({
    queryKey: ["/api/quotes/recent"],
  });

  const isLoading = statsLoading || quotesLoading;

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  const formatCurrency = (value: number | string) => formatMoney(value, user?.market, { decimals: 0 });

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold flex items-center gap-2 flex-wrap" data-testid="text-dashboard-title">
            Dashboard
            <HelpTip testId="button-help-dashboard" content="Your dashboard shows a summary of all your quoting activity, including total quotes, clients, revenue, and acceptance rate. The Time & Money Saved widget tracks how much The Proper Tool saves you compared to traditional quoting." />
          </h1>
          <p className="text-muted-foreground">Welcome back! Here's an overview of your quoting activity.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={openQuoteModal} data-testid="button-dashboard-new-quote">
            <Plus className="h-4 w-4 mr-2" />
            New Quote
          </Button>
        </div>
      </div>

      {/* Tutorial tip */}
      <TutorialTip id="dashboard-welcome" title="Welcome to The Proper Tool!">
        Start with <strong>Auto Quote</strong>. Upload your drawings, photos, or PDF plans and our smart quoting engine builds a full itemised quote. If you don't have files to hand, describing the job in plain English is a good starting point. <strong>Template Quote</strong> starts from a pre-built structure for common job types, and <strong>Manual Quote</strong> gives you full line-by-line control. All three produce a branded PDF ready to send to your client.
      </TutorialTip>

      {/* Plan & Usage */}
      <PlanUsageCard />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Quotes</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <FitText className="font-bold" maxSize={30} data-testid="text-stat-quotes">
              {stats?.totalQuotes || 0}
            </FitText>
            <p className="text-xs text-muted-foreground mt-1">All time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Clients</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <FitText className="font-bold" maxSize={30} data-testid="text-stat-clients">
              {stats?.totalClients || 0}
            </FitText>
            <p className="text-xs text-muted-foreground mt-1">Active clients</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Quote Value</CardTitle>
            <PoundSterling className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <FitText className="font-bold" maxSize={30} data-testid="text-stat-value">
              {formatCurrency(stats?.totalValue || 0)}
            </FitText>
            <p className="text-xs text-muted-foreground mt-1">Total quoted value</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Acceptance Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <FitText className="font-bold" maxSize={30} data-testid="text-stat-rate">
              {stats?.acceptedRate || 0}%
            </FitText>
            <p className="text-xs text-muted-foreground mt-1">Quotes accepted</p>
          </CardContent>
        </Card>
      </div>

      {/* Time & Money Saved */}
      <TimeSavingsWidget />

      {/* Referral Programme */}
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
                <Gift className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold">Earn with every referral</h3>
                <p className="text-sm text-muted-foreground">Refer a builder and earn up to {getMarketCurrency(user?.market).symbol}20/month for every active subscription they take out, for up to 12 months.</p>
              </div>
            </div>
            <Button variant="outline" size="sm" className="self-start sm:self-center flex-shrink-0 border-primary/40 text-primary hover:bg-primary/10" asChild data-testid="button-referral-dashboard">
              <Link href="/referrals">
                View my referrals
                <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Quotes */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="text-base sm:text-lg">Recent Quotes</CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link href="/quotes" data-testid="link-view-all-quotes">
              View all
              <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {!recentQuotes || recentQuotes.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No quotes yet"
              description="Create your first quote to get started with professional pricing."
              actionLabel="Create Quote"
              onAction={openQuoteModal}
            />
          ) : (
            <div className="space-y-4">
              {recentQuotes.map((quote) => (
                <Link
                  key={quote.id}
                  href={`/quotes/${quote.id}`}
                  className="flex items-center gap-4 p-3 rounded-lg hover-elevate cursor-pointer"
                  data-testid={`link-quote-${quote.id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{quote.quoteNumber}</span>
                      <StatusBadge status={quote.status || "draft"} />
                    </div>
                    <div className="text-sm text-muted-foreground truncate">
                      {quote.clientName || "No client"} • {quote.projectType?.replace(/_/g, " ") || "Unspecified"}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-semibold">{formatCurrency(quote.grandTotal || 0)}</div>
                    <div className="text-xs text-muted-foreground flex items-center justify-end gap-1">
                      <Clock className="h-3 w-3" />
                      {quote.createdAt ? new Date(quote.createdAt).toLocaleDateString("en-GB") : "N/A"}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="hover-elevate cursor-pointer" onClick={openQuoteModal}>
          <CardContent className="pt-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Plus className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">Create Quote</h3>
              <p className="text-sm text-muted-foreground">Start a new project quote</p>
            </div>
          </CardContent>
        </Card>
        <Card className="hover-elevate cursor-pointer" onClick={() => window.location.href = "/clients"}>
          <CardContent className="pt-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">Manage Clients</h3>
              <p className="text-sm text-muted-foreground">View and add clients</p>
            </div>
          </CardContent>
        </Card>
        <Card className="hover-elevate cursor-pointer" onClick={() => window.location.href = "/cost-library"}>
          <CardContent className="pt-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">Cost Library</h3>
              <p className="text-sm text-muted-foreground">Edit your pricing rates</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <NewQuoteModal open={quoteModalOpen} onClose={closeQuoteModal} />
    </div>
  );
}
